import { useGesture } from '@hooks/useGesture'
import { useCallback, useState } from 'react'

export type SwipeState = {
  direction: 'left' | 'right' | null
  progress: number
  isActive: boolean
  isTriggered: boolean
}

/**
 * Custom hook to manage touch swipe gestures for page navigation.
 *
 * @param onNextPage - Function called when user swipes right-to-left
 * @param onPreviousPage - Function called when user swipes left-to-right
 * @returns Current swipe state for animations
 */
export const useDuaSwipeNavigation = (
  onNextPage: () => void,
  onPreviousPage: () => void,
) => {
  const [swipeState, setSwipeState] = useState<SwipeState>({
    direction: null,
    progress: 0,
    isActive: false,
    isTriggered: false,
  })

  const handleSwipeEnd = useCallback(
    (direction: 'left' | 'right') => {
      setSwipeState((prev) => ({ ...prev, isTriggered: true, progress: 1 }))

      if (direction === 'left') {
        onPreviousPage()
      } else {
        onNextPage()
      }

      // Reset swipe state after animation completes
      setTimeout(() => {
        setSwipeState({
          direction: null,
          progress: 0,
          isActive: false,
          isTriggered: false,
        })
      }, 500)
    },
    [onNextPage, onPreviousPage],
  )

  useGesture({
    onSwipeLeft: () => handleSwipeEnd('left'),
    onSwipeRight: () => handleSwipeEnd('right'),
    onSwipeMove: (direction, progress) => {
      setSwipeState({ direction, progress, isActive: true, isTriggered: false })
    },
    onSwipeCancel: () => {
      setSwipeState({
        direction: null,
        progress: 0,
        isActive: false,
        isTriggered: false,
      })
    },
  })

  return { swipeState }
}
